import React from "react";
import { motion } from "framer-motion";
import { 
  Crown, 
  Star, 
  Gift, 
  TrendingUp, 
  Coins, 
  ExternalLink,
  Users,
  Heart,
  Sparkles,
  Trophy,
  Briefcase,
  Zap
} from "lucide-react";

const Premium = () => {
  // Animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const premiumFeatures = [
    {
      icon: Crown,
      title: "Golden Premium Rank",
      description: "Get the exclusive golden premium rank on our support server",
      color: "from-yellow-400 to-orange-500"
    },
    {
      icon: Sparkles,
      title: "Monthly Spin",
      description: "Unlock access to the special monthly spin with exclusive rewards",
      color: "from-purple-400 to-pink-500"
    },
    {
      icon: Gift,
      title: "3 Free Lotto Tickets",
      description: "Get 3 bonus lotto tickets with your daily claim every day",
      color: "from-green-400 to-teal-500"
    },
    {
      icon: Briefcase,
      title: "Premium Work Jobs",
      description: "Access exclusive premium-only work jobs with higher payouts",
      color: "from-blue-400 to-indigo-500"
    },
    {
      icon: Zap,
      title: "2.5x Daily Spin",
      description: "Boost your daily spin rewards with a 2.5x multiplier",
      color: "from-red-400 to-pink-500"
    },
    {
      icon: TrendingUp,
      title: "+5% Market Crash Protection",
      description: "Get an additional 5% bonus protection during market crashes",
      color: "from-cyan-400 to-blue-500"
    },
    {
      icon: Trophy,
      title: "Premium Business",
      description: "Unlock access to premium-only business opportunities",
      color: "from-purple-500 to-indigo-500"
    },
    {
      icon: Coins,
      title: "+$200k Voting Bonus",
      description: "Earn an additional $200,000 bonus when you vote for Voxel",
      color: "from-yellow-500 to-orange-400"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white pt-16">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-purple-950/30 to-blue-950/30"></div>
        
        {/* Retro Grid Background */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `
            linear-gradient(rgba(147, 51, 234, 0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(147, 51, 234, 0.3) 1px, transparent 1px)
          `,
          backgroundSize: '32px 32px'
        }}></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="initial"
            animate="animate"
            variants={stagger}
          >
            <motion.div
              variants={fadeInUp}
              className="flex justify-center mb-8"
            >
              <div className="relative">
                <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-2xl shadow-purple-500/25 pixel-border">
                  <Crown className="w-12 h-12 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center">
                  <Star className="w-4 h-4 text-slate-900 fill-current" />
                </div>
              </div>
            </motion.div>

            <motion.h1 
              variants={fadeInUp}
              className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-purple-400 via-pink-400 to-purple-500 bg-clip-text text-transparent"
            >
              Voxel Premium
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed"
            >
              Unlock exclusive features and supercharge your Voxel experience
            </motion.p>

            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12"
            >
              <a
                href="https://ko-fi.com/voxelbot"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-6 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-300 transform hover:scale-105 shadow-2xl shadow-purple-500/25 text-center"
              >
                <div className="text-xl font-bold">Join now for £2.99</div>
                <div className="text-sm opacity-90">per month</div>
                
                {/* Button shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-opacity rounded-lg"></div>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-16 bg-slate-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center"
          >
            <div className="bg-blue-900/30 border border-blue-500/30 rounded-2xl p-8 mb-8">
              <div className="flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-blue-400 mr-3" />
                <h3 className="text-2xl font-bold text-blue-400">Important: Join Our Server First!</h3>
              </div>
              <p className="text-lg text-gray-300 mb-6">
                Before purchasing premium, you <strong>must</strong> join our support server to receive your premium rank and access all features.
              </p>
              <a
                href="https://discord.com/invite/CfnpERCY7r"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-200 transform hover:scale-105"
              >
                <Users className="w-5 h-5" />
                Join Support Server First
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"
            >
              Premium Features
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-xl text-gray-400 max-w-2xl mx-auto"
            >
              Unlock these exclusive benefits and take your Voxel experience to the next level
            </motion.p>
          </motion.div>

          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {premiumFeatures.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="group relative bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 hover:border-purple-500/50 transition-all duration-300 overflow-hidden"
              >
                <div className="relative z-10">
                  <div className="flex justify-center mb-4">
                    <div className={`w-12 h-12 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center shadow-lg`}>
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-bold mb-3 text-white group-hover:text-purple-400 transition-colors text-center">
                    {feature.title}
                  </h3>
                  
                  <p className="text-gray-400 text-sm leading-relaxed text-center">
                    {feature.description}
                  </p>
                </div>

                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Personal Message Section */}
      <section className="py-20 bg-slate-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center"
          >
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
              <div className="flex items-center justify-center mb-6">
                <Heart className="w-8 h-8 text-red-400 mr-3" />
                <h3 className="text-2xl font-bold">A Message from the Developer</h3>
              </div>
              
              <div className="text-lg text-gray-300 leading-relaxed space-y-4">
                <p>
                  Hi there! Voxel is made by just one person (me!) and I pour my heart into making it the best Discord economy bot possible.
                </p>
                <p>
                  While supporting the bot isn't necessary, it definitely helps cover all the costs of running and improving Voxel. From server hosting to API fees, every bit of support goes directly back into making Voxel better for everyone.
                </p>
                <p>
                  I genuinely appreciate all our community members, whether you're a free user or a premium supporter. <strong>Please only become a premium member if you're able to do so comfortably</strong> - your enjoyment of Voxel is what matters most to me.
                </p>
                <p className="text-purple-400 font-semibold">
                  Thank you for being part of the Voxel family! 💜
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/20 to-pink-900/20"></div>
        
        <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 z-10">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              Ready to go Premium?
            </motion.h2>
            
            <motion.p 
              variants={fadeInUp}
              className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto"
            >
              Join the premium member family and unlock the full Voxel experience
            </motion.p>

            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <a
                href="https://discord.com/invite/CfnpERCY7r"
                target="_blank"
                rel="noopener noreferrer"
                className="group border-2 border-blue-500 text-blue-400 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-500 hover:text-white transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Users className="w-5 h-5" />
                1. Join Support Server
                <ExternalLink className="w-4 h-4" />
              </a>
              
              <a
                href="https://ko-fi.com/voxelbot"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-300 transform hover:scale-105 shadow-2xl shadow-purple-500/25 flex items-center justify-center gap-2"
              >
                <Crown className="w-5 h-5" />
                2. Get Premium (£2.99/month)
                <ExternalLink className="w-4 h-4" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Premium;